import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { useToast } from '../../components/ui/use-toast';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useT } from '../../context/LanguageContext';
import loyaltyService from '../../services/loyaltyService';
import { Award, Gift, CreditCard, Settings, Users, AlertTriangle } from 'lucide-react';

/*
 * Loyalty Manager — the programme's cockpit. Reads ONE summary payload and
 * lays it out; every figure here is served by the backend, never derived on
 * screen, so this page and the reports can't disagree.
 */

const fmtPts = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function LoyaltyManager() {
  const { t } = useT();
  const { toast } = useToast();
  const { formatCurrency } = useAppSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loyaltyService.getSummary()
      .then(setData)
      .catch((err) => toast({ title: 'Error', description: err.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [toast]);

  if (loading) return <div className="p-6 text-gray-500">{t('loyalty.common.loading')}</div>;
  if (!data) return <div className="p-6 text-gray-500">{t('loyalty.common.none')}</div>;

  const p = data.program;
  const tiles = [
    [t('loyalty.dash.membersCount'), fmtPts(data.members_count), `+${data.new_members_30d} · ${t('loyalty.dash.new30d')}`],
    [t('loyalty.dash.pointsOut'), fmtPts(data.points_outstanding),
      `${t('loyalty.dash.earned')} ${fmtPts(data.points_earned_total)} · ${t('loyalty.dash.redeemed')} ${fmtPts(data.points_redeemed_total)} · ${t('loyalty.dash.expired')} ${fmtPts(data.points_expired_total)}`],
    [t('loyalty.dash.memberSpend'), formatCurrency(Number(data.member_spend_total) || 0), null],
    [t('loyalty.dash.vouchersActive'), fmtPts(data.vouchers_active),
      `${fmtPts(data.vouchers_redeemed)} ${t('loyalty.dash.redeemed').toLowerCase()} · ${formatCurrency(Number(data.voucher_value_given) || 0)}`],
    [t('loyalty.dash.giftLiability'), formatCurrency(Number(data.gift_card_liability) || 0),
      `${fmtPts(data.gift_cards_active)} ${t('loyalty.gc.status.ACTIVE').toLowerCase()}`],
    [t('loyalty.dash.pendingLeads'), fmtPts(data.pending_leads), null],
  ];

  const quickLinks = [
    { to: '/loyalty/members', icon: Users, label: t('loyalty.members') },
    { to: '/loyalty/rewards', icon: Gift, label: t('loyalty.rewards') },
    { to: '/loyalty/gift-cards', icon: CreditCard, label: t('loyalty.giftCards') },
    { to: '/settings/loyalty', icon: Settings, label: t('loyalty.settings') },
  ];

  return (
    <div className="p-6 w-full">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <Award className="h-7 w-7 text-amber-600" /> {t('loyalty.manager')}
          </h1>
          <p className="text-gray-600 mt-1">
            {p.name}{p.tagline ? ` — ${p.tagline}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {quickLinks.map(({ to, icon: Icon, label }) => (
            <Link key={to} to={to}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-foreground hover:bg-accent">
              <Icon size={15} /> {label}
            </Link>
          ))}
        </div>
      </div>

      {!p.is_enabled && (
        <Card className="p-4 mb-4 border-amber-300 bg-amber-50 text-amber-800 flex items-center gap-2">
          <AlertTriangle size={18} className="shrink-0" />
          <span className="text-sm">{t('loyalty.dash.programOff')}</span>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {tiles.map(([label, value, hint]) => (
          <Card key={label} className="p-4">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-2xl font-semibold text-gray-900 mt-0.5">{value}</div>
            {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {/* Members by tier */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3">{t('loyalty.dash.tierSplit')}</h3>
          {data.tier_split.length === 0 ? (
            <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
          ) : (
            <div className="space-y-2">
              {data.tier_split.map((row) => {
                const max = Math.max(...data.tier_split.map((r) => r.count), 1);
                return (
                  <div key={row.name} className="flex items-center gap-2">
                    <span className="w-24 shrink-0 text-sm text-gray-700 truncate">{row.name}</span>
                    <div className="flex-1 h-4 rounded bg-gray-100 overflow-hidden">
                      <div className="h-full rounded"
                        style={{ width: `${(row.count / max) * 100}%`, backgroundColor: row.colour || '#C9A227' }} />
                    </div>
                    <span className="w-10 text-right text-sm tabular-nums text-gray-600">{row.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Top members */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-800 mb-3">{t('loyalty.dash.topMembers')}</h3>
          {data.top_members.length === 0 ? (
            <p className="text-sm text-gray-500">{t('loyalty.common.none')}</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left font-medium py-1.5 pr-4">{t('loyalty.common.name')}</th>
                  <th className="text-left font-medium py-1.5 pr-4">{t('loyalty.member.tier')}</th>
                  <th className="text-right font-medium py-1.5">{t('loyalty.member.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.top_members.map((m) => (
                  <tr key={m.id}>
                    <td className="py-1.5 pr-4">
                      <Link to={`/loyalty/members/${m.id}`} className="font-medium text-gray-900 hover:underline">
                        {m.name}
                      </Link>
                      <span className="ml-2 font-mono text-[11px] text-gray-400">{m.membership_number}</span>
                    </td>
                    <td className="py-1.5 pr-4">
                      {m.tier ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: m.tier.colour || '#64748B' }}>
                          {m.tier.name}
                        </span>
                      ) : <span className="text-gray-400 text-xs">{t('loyalty.member.noTier')}</span>}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{fmtPts(m.points_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
